import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isType } from '@sorare/core/src/lib/gql';
import { isRarityStr } from '@sorare/core/src/lib/so5';

import { PlayerWithSupply } from '#src/components/rewards/PlayerWithSupply/index.tsx';

import type {
  RewardsFixtureLeagueQualityRarityPageQuery,
  RewardsFixtureLeagueQualityRarityPageQueryVariables,
} from './__generated__/index.graphql.ts';

const REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY_PAGE_QUERY = gql`
  query RewardsFixtureLeagueQualityRarityPageQuery(
    $league: String!
    $rarity: String!
    $quality: String!
  ) {
    so5 {
      so5LeaderboardGroupInterface(slug: $league) {
        slug
        ... on So5League {
          slug
          rewardPool(rarity: $rarity, quality: $quality) {
            slug
            ...PlayerWithSupply_playerWithSupply
          }
        }
        ... on So5League {
          slug
          ...PlayerWithSupply_so5League
        }
      }
    }
  }
  ${PlayerWithSupply.fragments.playerWithSupply}
  ${PlayerWithSupply.fragments.so5League}
` as TypedDocumentNode<
  RewardsFixtureLeagueQualityRarityPageQuery,
  RewardsFixtureLeagueQualityRarityPageQueryVariables
>;

type Props = {
  league: string;
  quality: string;
  rarity: string;
};

export const RewardsFixtureLeagueQualityRarityPage = ({
  league,
  quality,
  rarity,
}: Props) => {
  const { data, loading } = useQuery(
    REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY_PAGE_QUERY,
    {
      variables: { league, quality: quality.toLowerCase(), rarity },
    }
  );

  const { so5LeaderboardGroupInterface: so5League } = data?.so5 || {};

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!so5League || !isType(so5League, 'So5League') || !isRarityStr(rarity)) {
    return null;
  }

  return (
    <PlayerWithSupply
      so5League={so5League}
      players={so5League.rewardPool}
      rarity={rarity as Rarity}
    />
  );
};
